"use client";

import { createContext, PropsWithChildren, useContext, useEffect, useRef } from "react";
import { HttpTypes } from "@medusajs/types";
import { useRegion } from "@/hooks/useRegion";
import { sdk } from "@/lib/medusa/client";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useCustomer } from "@/hooks/useCustomer";
import { getCartQuantityUnit, normalizeCartLineQuantity, resolveProductConditioning } from "@/lib/product-units";
import { resolveCartAddresses } from "@/lib/cart-addresses";

type CartContextType = {
  cart?: HttpTypes.StoreCart;
  setCart: (cart: HttpTypes.StoreCart) => void;
  refreshCart: () => void;
  isCalculatingTaxes?: boolean;
};

const CartContext = createContext<CartContextType | null>(null);

// Cart key
export const cartQueryKeys = {
  cart: (cartId: string) => ["cart", cartId] as const,
};

const migrateLegacyCartItems = async (cart: HttpTypes.StoreCart) => {
  let migratedCart = cart;

  for (const item of cart.items || []) {
    if (!item.product_id) {
      throw new Error(`Cannot migrate cart line ${item.id}: product is missing`);
    }

    const { product } = await sdk.store.product.retrieve(item.product_id, {
      fields: "+metadata",
    });

    const conditioning = resolveProductConditioning(
      product.metadata?.conditioning,
      product.metadata?.conditioning_bac
    );
    const quantityUnit = getCartQuantityUnit(conditioning);
    const quantity = normalizeCartLineQuantity(item.quantity, item.metadata?.quantity_unit, conditioning);

    if (item.metadata?.quantity_unit === quantityUnit && item.quantity === quantity) continue;

    const { cart: updatedCart } = await sdk.store.cart.updateLineItem(cart.id, item.id, {
      quantity,
      metadata: {
        ...item.metadata,
        quantity_unit: quantityUnit,
      },
    });

    migratedCart = updatedCart;
  }

  return migratedCart;
};

export const CartProvider = ({ children }: PropsWithChildren) => {
  const { region } = useRegion();
  const { customer } = useCustomer();
  const queryClient = useQueryClient();

  const cartId = typeof window !== "undefined" ? localStorage.getItem("cart_id") : "";

  const { data: cart } = useQuery({
    queryKey: cartId ? cartQueryKeys.cart(cartId) : ["cart", "empty"],
    queryFn: async () => {
      if (!cartId) {
        const { cart } = await sdk.store.cart.create({ region_id: region?.id });
        localStorage.setItem("cart_id", cart.id);
        return cart;
      }

      const { cart } = await sdk.store.cart.retrieve(cartId);

      if (!cart) {
        localStorage.removeItem("cart_id");

        const { cart } = await sdk.store.cart.create({ region_id: region?.id });
        localStorage.setItem("cart_id", cart.id);

        return cart;
      }

      return migrateLegacyCartItems(cart);
    },
    enabled: !!region,
  });

  const taxCalculationMutation = useMutation({
    mutationFn: async (cartId: string) => {
      const defaultCountryCode = region?.countries?.[0]?.iso_2 || "be";

      const { shippingAddress, billingAddress } = resolveCartAddresses({
        shippingAddress: cart?.shipping_address,
        billingAddress: cart?.billing_address,
        fallbackAddress: customer?.addresses?.[0],
        firstName: customer?.first_name,
        lastName: customer?.last_name,
        phone: customer?.phone,
        defaultCountryCode,
      });

      await sdk.store.cart.update(cartId, {
        shipping_address: shippingAddress,
        billing_address: billingAddress,
      });

      const res = await sdk.client.fetch(`store/carts/${cartId}/taxes`, {
        method: "POST",
      });

      return (res as { cart: HttpTypes.StoreCart }).cart;
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData(cartQueryKeys.cart(updatedCart.id), updatedCart);
    },
    onError: (error: Error) => {
      console.error("Error calculating taxes:", error);
    },
  });

  const hasCalculatedTaxes = useRef(false);

  useEffect(() => {
    if (
      cart?.id &&
      (cart?.items?.length ?? 0) > 0 &&
      !taxCalculationMutation.isPending &&
      !hasCalculatedTaxes.current
    ) {
      const needsTaxCalculation = !cart.tax_total || cart.tax_total === 0;

      const hasValidShippingAddress = cart.shipping_address?.country_code;
      const hasValidBillingAddress = cart.billing_address?.country_code;

      if (needsTaxCalculation || !hasValidShippingAddress || !hasValidBillingAddress) {
        hasCalculatedTaxes.current = true;
        taxCalculationMutation.mutate(cart.id);
      }
    }
  }, [cart, taxCalculationMutation]);

  const setCart = (updatedCart: HttpTypes.StoreCart) => {
    queryClient.setQueryData(cartQueryKeys.cart(updatedCart.id), updatedCart);
  };

  const refreshCart = () => {
    if (cartId) {
      queryClient.removeQueries({ queryKey: cartQueryKeys.cart(cartId) });
    }
    localStorage.removeItem("cart_id");
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        refreshCart,
        isCalculatingTaxes: taxCalculationMutation.isPending,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
